import { Text } from "@mantine/core";
import { motion } from "motion/react";
import { type ReactNode } from "react";

export interface JoinTheClubProps extends BoxProps {
  headline: ReactNode;
}

const motionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const JoinTheClub: FC<JoinTheClubProps> = ({ headline, ...otherProps }) => {
  const { submit, getInputProps, values, processing } = useFetchForm({
    action: routes.interestSubmissions.create,
    descriptor: "join the club",
    initialValues: {
      name: "",
      email: "",
      feedback_contact: "",
      referral_contact: "",
    },
    transformValues: values => ({ interest_submission: values }),
    onSuccess: () => {
      router.visit(routes.landing.show.path(), {
        onSuccess: () => {
          toast.success("Thank you for joining us 🫡", {
            description: "You're all set—welcome to the club.",
            duration: Infinity,
          });
        },
      });
    },
  });
  const filled = useFieldsFilled(values, "name", "email");
  return (
    <Box
      component={motion.div}
      variants={{
        visible: {
          transition: {
            staggerChildren: 1.6,
          },
        },
      }}
      initial="hidden"
      animate="visible"
      {...otherProps}
    >
      <Stack>
        <Stack gap={6}>
          <Text
            component={motion.p}
            size="lg"
            lh="xs"
            fw={500}
            variants={motionVariants}
            ta="center"
          >
            {headline}
          </Text>
        </Stack>
        <motion.form
          variants={{
            hidden: { opacity: 0, y: 300 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                type: "spring",
                stiffness: 200,
                mass: 0.2,
                damping: 20,
                duration: 0.6,
              },
            },
          }}
          onSubmit={submit}
        >
          <Card withBorder>
            <Text fw={700} size="xl" ta="center" lh="xs">
              You&apos;re invited to{" "}
              <span style={{ fontWeight: 900 }}>join the club</span>
            </Text>
            <Text size="sm" ta="center" c="dark.1" lh="xs">
              Because great relationships are built.
            </Text>
            <Stack gap={8} mt="xs">
              <TextInput
                {...getInputProps("name")}
                label="Your name"
                leftSection={<UserIcon />}
                required
              />
              <TextInput
                {...getInputProps("email")}
                label="Your email"
                leftSection={<EmailIcon />}
                placeholder="email@example.com"
                required
              />
              <Stack gap={6}>
                <TextInput
                  {...getInputProps("feedback_contact")}
                  label="Can we ask you for feedback?"
                />
                <Text size="xs" c="dimmed" inline>
                  Your phone #, Twitter handle, or Instagram handle.
                </Text>
              </Stack>
              <Stack gap={6}>
                <TextInput
                  {...getInputProps("referral_contact")}
                  label="Who is a friend that should know about this?"
                  description="There's no need to go this journey alone—bring a friend along for the ride."
                  placeholder="@friendstagram"
                />
                <Text size="xs" c="dimmed" inline>
                  Their email, Twitter handle, or Instagram handle. We'll reach
                  out.
                </Text>
              </Stack>
              <Button
                type="submit"
                mt="xs"
                variant="white"
                size="md"
                disabled={!filled}
                loading={processing}
              >
                Let's do this.
              </Button>
            </Stack>
          </Card>
        </motion.form>
      </Stack>
    </Box>
  );
};

export default JoinTheClub;
